/**
 * AddAnimeModal Component
 * URL入力から新規アニメを追加（自動メタデータ取得）
 */

import { useState, useEffect, useRef } from 'react'
import { Plus, Link, Tv, Search } from 'lucide-react'
import Modal from './Modal'
import { detectPlatform, getPlatformName } from '../utils/parser'
import { fetchAnimeMetadata, parseSchedule, searchAnime } from '../services/jikan'

export function AddAnimeModal({ isOpen, onClose, onAdd, onAdded }) {
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [platform, setPlatform] = useState(null)
  const [loading, setLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [searchPreview, setSearchPreview] = useState(null)
  const [isSearching, setIsSearching] = useState(false)
  const titleInputRef = useRef(null)

  // URL貼り付け時にタイトル欄へ自動フォーカス
  const handleUrlPaste = (e) => {
    setUrl(e.target.value)
    // 貼り付け後、500ms後にタイトル欄へフォーカス
    setTimeout(() => {
      titleInputRef.current?.focus()
    }, 500)
  }

  // URLからプラットフォームを自動判定
  useEffect(() => {
    if (url) {
      const detected = detectPlatform(url)
      setPlatform(detected)
    } else {
      setPlatform(null)
    }
  }, [url])

  // タイトル入力で検索プレビュー（debounce）
  useEffect(() => {
    if (!title.trim() || title.trim().length < 2) {
      setSearchPreview(null)
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const results = await searchAnime(title.trim())
        setSearchPreview(results.slice(0, 5)) // 上位5件を表示
      } catch (err) {
        console.warn('Search preview error:', err)
        setSearchPreview(null)
      } finally {
        setIsSearching(false)
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [title])

  // プレビューから選択 → 即追加
  const handleSelectPreview = async (anime) => {
    if (!url.trim()) {
      alert('URLを入力してください')
      return
    }

    setLoading(true)
    setStatusMessage('追加中...')
    setSearchPreview(null)

    try {
      // 放送スケジュール情報
      let broadcastDay = null
      let airingStatus = anime.airing ? 'airing' : 'finished'

      if (anime.broadcast?.day) {
        broadcastDay = anime.broadcast.day
      }

      const newAnime = {
        id: `anime_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        title: anime.title_japanese || anime.title,
        url: url.trim(),
        platform: platform || 'other',
        currentEpisode: 0,
        totalEpisodes: anime.episodes || null,
        status: 'plan_to_watch',
        season: 'シーズン1',
        daveScore: 0,
        daveScoreDetail: {
          axis_story: 0,
          axis_visual: 0,
          axis_excitement: 0,
          axis_emotion: 0,
          axis_fluency: 0
        },
        notes: '',
        metadata: {
          malId: anime.mal_id,
          title: anime.title,
          titleJapanese: anime.title_japanese,
          imageUrl: anime.images?.jpg?.image_url || null,
          episodes: anime.episodes,
          status: anime.status,
          airing: anime.airing,
          score: anime.score,
          synopsis: anime.synopsis,
          studios: anime.studios?.map(s => s.name) || [],
          genres: anime.genres?.map(g => g.name) || []
        },
        airingStatus,
        broadcastDay,
        nextEpisodeDate: null,
        lastUpdated: new Date().toISOString()
      }

      if (onAdd) {
        await onAdd(newAnime)
      }

      // リセットして閉じる
      setUrl('')
      setTitle('')
      setPlatform(null)
      setStatusMessage('')

      if (onAdded) {
        onAdded()
      }
      if (onClose) {
        onClose()
      }
    } catch (err) {
      console.error('Add error:', err)
      alert(`追加エラー: ${err.message}`)
    } finally {
      setLoading(false)
      setStatusMessage('')
    }
  }

  const handleSubmit = async () => {
    if (!url.trim()) {
      alert('URLを入力してください')
      return
    }
    if (!title.trim()) {
      alert('タイトルを入力してください')
      return
    }

    setLoading(true)
    setStatusMessage('アニメ情報を検索中...')

    try {
      // Jikan APIでメタデータを取得
      let metadata = null
      try {
        metadata = await fetchAnimeMetadata(title.trim())
      } catch (apiErr) {
        console.warn('Jikan API error:', apiErr)
        // APIエラーでも続行（メタデータなしで追加）
      }

      setStatusMessage('追加中...')

      // 放送スケジュール情報
      let broadcastDay = null
      let nextEpisodeDate = null
      let airingStatus = null

      if (metadata?.broadcast) {
        const schedule = parseSchedule(metadata.broadcast)
        if (schedule) {
          broadcastDay = schedule.dayName
        }
      }

      if (metadata) {
        airingStatus = metadata.airing ? 'airing' : 'finished'
      }

      const newAnime = {
        id: `anime_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        title: metadata?.titleJapanese || metadata?.title || title.trim(),
        url: url.trim(),
        platform: platform || 'other',
        currentEpisode: 0,
        totalEpisodes: metadata?.episodes || null,
        status: 'plan_to_watch',
        season: 'シーズン1',
        daveScore: 0,
        daveScoreDetail: {
          axis_story: 0,
          axis_visual: 0,
          axis_excitement: 0,
          axis_emotion: 0,
          axis_fluency: 0
        },
        notes: '',
        metadata: metadata || null,
        airingStatus,
        broadcastDay,
        nextEpisodeDate,
        lastUpdated: new Date().toISOString()
      }

      console.log('Adding anime:', newAnime.title)

      if (onAdd) {
        await onAdd(newAnime)
        console.log('onAdd completed')
      } else {
        console.error('onAdd is not defined!')
      }

      // リセットして閉じる
      setUrl('')
      setTitle('')
      setPlatform(null)
      setStatusMessage('')
      setSearchPreview(null)

      // 追加完了コールバック（タブ切替用）
      if (onAdded) {
        onAdded()
      }

      if (onClose) {
        onClose()
      }
    } catch (err) {
      console.error('Add error:', err)
      alert(`追加エラー: ${err.message}`)
    } finally {
      setLoading(false)
      setStatusMessage('')
    }
  }

  const handleClose = () => {
    setUrl('')
    setTitle('')
    setPlatform(null)
    setSearchPreview(null)
    onClose?.()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="新規追加"
      footer={
        <>
          <button className="btn btn-secondary" onClick={handleClose} disabled={loading}>
            キャンセル
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !url.trim() || !title.trim()}
          >
            {loading ? <Search size={16} className="spin" /> : <Plus size={16} />}
            {loading ? statusMessage || '検索中...' : '追加'}
          </button>
        </>
      }
    >
      {/* URL入力 */}
      <div className="form-group">
        <label className="form-label">
          <Link size={14} style={{ marginRight: '4px' }} />
          視聴URL
        </label>
        <input
          type="url"
          className="form-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onPaste={handleUrlPaste}
          placeholder="https://www.netflix.com/watch/..."
          autoFocus
        />
        {platform && (
          <div style={{
            marginTop: '8px',
            fontSize: '0.8rem',
            color: 'var(--accent-cyan)'
          }}>
            <Tv size={12} style={{ marginRight: '4px' }} />
            プラットフォーム検出: <strong>{getPlatformName(platform)}</strong>
          </div>
        )}
      </div>

      {/* タイトル入力 */}
      <div className="form-group">
        <label className="form-label">
          <Search size={14} style={{ marginRight: '4px' }} />
          タイトル（検索用）
        </label>
        <input
          ref={titleInputRef}
          type="text"
          className="form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="作品名を入力（例：ワルキューレ）"
        />

        {/* 検索プレビュー */}
        {isSearching && (
          <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '8px' }}>
            検索中...
          </div>
        )}
        {searchPreview && searchPreview.length > 0 && !isSearching && (
          <div style={{
            marginTop: '8px',
            border: '1px solid var(--glass-border)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              fontSize: '0.7rem',
              padding: '4px 8px',
              background: 'var(--glass-bg)',
              opacity: 0.7
            }}>
              検索結果（クリックで選択）
            </div>
            {searchPreview.map((anime, idx) => (
              <div
                key={anime.mal_id}
                onClick={() => handleSelectPreview(anime)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 8px',
                  cursor: 'pointer',
                  borderTop: idx > 0 ? '1px solid var(--glass-border)' : 'none',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--glass-bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {anime.images?.jpg?.small_image_url && (
                  <img
                    src={anime.images.jpg.small_image_url}
                    alt=""
                    style={{
                      width: '28px',
                      height: '40px',
                      objectFit: 'cover',
                      borderRadius: '3px'
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {anime.title_japanese || anime.title}
                  </div>
                  <div style={{ fontSize: '0.65rem', opacity: 0.6 }}>
                    {anime.episodes ? `${anime.episodes}話` : '話数不明'}
                    {anime.year && ` • ${anime.year}年`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {searchPreview && searchPreview.length === 0 && !isSearching && (
          <div style={{ fontSize: '0.8rem', color: 'var(--accent-red)', marginTop: '8px' }}>
            見つかりませんでした。別のキーワードで試してください
          </div>
        )}
      </div>
    </Modal>
  )
}

export default AddAnimeModal
